/**
 * GlobalDocumentCitationTooltip Component
 *
 * A single tooltip instance rendered at root level that reads state from context.
 * This prevents flickering during streaming because the tooltip never unmounts.
 *
 * Place this component once at a high level (e.g., inside DocumentCitationTooltipProvider)
 * and it will display tooltip content for any DocumentCitationBubble that dispatches hover events.
 */

import React from "react";
import { createPortal } from "react-dom";
import type { DocumentCitationTooltipData } from "../../contexts/DocumentCitationTooltipContext";
import { useDocumentCitationTooltip } from "../../contexts/DocumentCitationTooltipContext";
import { getDocumentTheme } from "./DocumentCitationIcons";
import { useTooltipPosition } from "./hooks/useTooltipPosition";
import { usePinnedTooltipDismiss } from "./hooks/usePinnedTooltipDismiss";
import { CitationTooltipPager, CitationTooltipPages } from "./CitationGroupControls";
import { CitedTextExcerpt } from "./CitedTextExcerpt";

/** File-type icon for a document name. */
const DocumentTooltipIcon: React.FC<{ title: string }> = ({ title }) => {
  const { Icon } = getDocumentTheme(title);

  return <Icon />;
};

const DocumentTooltipName: React.FC<{ title: string }> = ({ title }) => (
  <div
    style={{
      fontSize: "12px",
      fontWeight: 500,
      color: "#212529",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    }}
  >
    {title}
  </div>
);

/** Quote and location for one citation. */
const DocumentTooltipBody: React.FC<{ item: DocumentCitationTooltipData }> = ({ item }) => (
  <>
    {/* Cited text excerpt: plain text, or a capped table for a sheet or a PDF page */}
    {item.citedText && <CitedTextExcerpt text={item.citedText} />}

    {/* Location info */}
    {item.location && (
      <div
        style={{
          fontSize: "10px",
          color: "#9E9E9E",
        }}
      >
        {item.location}
      </div>
    )}
  </>
);

/**
 * Global tooltip that reads from DocumentCitationTooltipContext.
 * Renders via portal for proper stacking.
 */
export const GlobalDocumentCitationTooltip: React.FC = () => {
  const { state, handleTooltipMouseEnter, handleTooltipMouseLeave, pageTooltip, closeTooltip } =
    useDocumentCitationTooltip();
  const { isVisible, anchorRect, documentTitle, citedText, location, group, pinned } = state;
  const { ref: tooltipRef, position } = useTooltipPosition<HTMLDivElement>(anchorRect, isVisible);

  usePinnedTooltipDismiss(isVisible && pinned, tooltipRef, group?.anchorEl ?? null, closeTooltip);

  // Don't render anything if not visible
  if (!isVisible) {
    return null;
  }

  // A merged chip's citations, paged; null for a single citation
  const pages = group && group.items.length > 1 ? group : null;

  const tooltipContent = (
    <div
      ref={tooltipRef}
      className="doc-citation-tooltip"
      onMouseEnter={handleTooltipMouseEnter}
      onMouseLeave={handleTooltipMouseLeave}
      role="tooltip"
      style={{
        position: "fixed",
        top: `${position.top}px`,
        left: `${position.left}px`,
        zIndex: 10000,
        maxWidth: "280px",
        minWidth: "180px",
        maxHeight: `${position.maxHeight}px`,
        overflowY: "auto",
        padding: "12px",
        backgroundColor: "#FFFFFF",
        border: "1px solid #E0E0E0",
        borderRadius: "8px",
        boxShadow: "0 4px 16px rgba(0, 0, 0, 0.12), 0 0 0 1px rgba(0, 0, 0, 0.05)",
        pointerEvents: "auto",
        fontFamily: "'Segoe UI', sans-serif",
        display: "flex",
        flexDirection: "column" as const,
        gap: "12px",
      }}
    >
      {/* Header row: icon container + document name */}
      {pages ? (
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          {/* One icon for the current page, not one per stacked page: Word.svg
              references its clipPath by a fixed id, which resolves to the
              first copy in the document; on a hidden page that clip is empty
              and would clip every later page's icon away. */}
          <DocumentTooltipIcon title={pages.items[pages.index].documentTitle} />
          <CitationTooltipPages index={pages.index} style={{ flex: 1, minWidth: 0 }}>
            {pages.items.map((item, i) => (
              <DocumentTooltipName key={i} title={item.documentTitle} />
            ))}
          </CitationTooltipPages>
          <CitationTooltipPager
            index={pages.index}
            total={pages.items.length}
            onPage={pageTooltip}
          />
        </div>
      ) : (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <DocumentTooltipIcon title={documentTitle} />
          <DocumentTooltipName title={documentTitle} />
        </div>
      )}

      {pages ? (
        <CitationTooltipPages index={pages.index}>
          {pages.items.map((item, i) => (
            <div key={i} style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
              <DocumentTooltipBody item={item} />
            </div>
          ))}
        </CitationTooltipPages>
      ) : (
        <DocumentTooltipBody item={{ documentTitle, citedText, location }} />
      )}
    </div>
  );

  // Render via portal to document body
  return createPortal(tooltipContent, document.body);
};

export default GlobalDocumentCitationTooltip;
